import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/backendURL';
import { showSuccess, showError } from '../../utils/notifications';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Eye,
  ArrowLeft,
  Send,
  Bug,
  Lightbulb,
  CreditCard,
  MessageSquare,
  Wrench,
  User,
  Clock,
  Mail,
  Filter,
  Search
} from 'lucide-react';

const SupportManagement = () => {
  const [view, setView] = useState('list'); // 'list', 'detail', or 'stats'
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [autobotStats, setAutobotStats] = useState(null);

  // Categories with icons
  const categories = {
    bug_report: {
      label: 'Bug Report',
      icon: Bug,
      color: 'text-red-600 bg-red-50'
    },
    feature_request: {
      label: 'Feature Request',
      icon: Lightbulb,
      color: 'text-yellow-600 bg-yellow-50'
    },
    billing_issue: {
      label: 'Billing Issue',
      icon: CreditCard,
      color: 'text-green-600 bg-green-50'
    },
    general_support: {
      label: 'General Support',
      icon: MessageSquare,
      color: 'text-blue-600 bg-blue-50'
    },
    technical_issue: {
      label: 'Technical Issue',
      icon: Wrench,
      color: 'text-purple-600 bg-purple-50'
    }
  };

  // Status badge styles
  const getStatusBadge = (status) => {
    const styles = {
      open: { label: 'Open', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
      needs_review: { label: 'Needs Review', color: 'bg-orange-100 text-orange-800 font-semibold' },
      resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800' },
      closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800' }
    };
    return styles[status] || styles.open;
  };

  // Priority badge styles
  const getPriorityBadge = (priority) => {
    const styles = {
      low: { label: 'Low', color: 'bg-gray-100 text-gray-800' },
      medium: { label: 'Medium', color: 'bg-blue-100 text-blue-800' },
      high: { label: 'High', color: 'bg-orange-100 text-orange-800' },
      urgent: { label: 'Urgent', color: 'bg-red-100 text-red-800' }
    };
    return styles[priority] || styles.medium;
  };

  // Fetch all tickets (admin)
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/ops/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      showError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  // Fetch autobot statistics
  const fetchAutobotStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/ops/support/autobot/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutobotStats(response.data);
    } catch (error) {
      console.error('Error fetching autobot stats:', error);
    }
  };

  // Fetch single ticket with replies
  const fetchTicketDetail = async (ticketId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/ops/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data);
      setView('detail');
    } catch (error) {
      console.error('Error fetching ticket:', error);
      showError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  // Update ticket status
  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${BACKEND_URL}/api/ops/support/tickets/${ticketId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Ticket status updated');
      fetchTicketDetail(ticketId);
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Failed to update ticket status');
    }
  };

  // Send admin reply
  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      showError('Please enter a message');
      return;
    }

    setSendingReply(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${BACKEND_URL}/api/ops/support/tickets/${selectedTicket.id}/replies`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess('Reply sent successfully. User will be notified via email and in-app.');
      setReplyMessage('');
      fetchTicketDetail(selectedTicket.id);
    } catch (error) {
      console.error('Error sending reply:', error);
      showError('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  useEffect(() => {
    if (view === 'list') {
      fetchTickets();
      fetchAutobotStats();
    }
  }, [view]);

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesSearch = 
      ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.ticket_number?.includes(searchQuery) ||
      ticket.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Render ticket list
  const renderTicketList = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Support Tickets</h2>
            <p className="text-gray-600 mt-1">Manage user support requests</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by ticket #, subject, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="needs_review">⚠️ Needs Review</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="text-sm text-gray-600">Total Tickets</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{tickets.length}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">Open</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">
              {tickets.filter(t => t.status === 'open').length}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">Needs Review</div>
            <div className="text-2xl font-bold text-orange-600 mt-1">
              {tickets.filter(t => t.status === 'needs_review').length}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">In Progress</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">
              {tickets.filter(t => t.status === 'in_progress').length}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">Resolved</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {tickets.filter(t => t.status === 'resolved').length}
            </div>
          </Card>
        </div>

        {/* Autobot Stats */}
        {autobotStats && (
          <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                🤖 AI Autobot Statistics
              </h3>
              <Badge className="bg-blue-100 text-blue-700">
                {autobotStats.autobot_stats.uptime}
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-600">Tickets Processed</div>
                <div className="text-xl font-bold text-gray-900">
                  {autobotStats.autobot_stats.tickets_processed}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Responses Sent</div>
                <div className="text-xl font-bold text-green-600">
                  {autobotStats.autobot_stats.responses_sent}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Marked for Review</div>
                <div className="text-xl font-bold text-orange-600">
                  {autobotStats.autobot_stats.marked_for_review}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Errors</div>
                <div className="text-xl font-bold text-red-600">
                  {autobotStats.autobot_stats.errors}
                </div>
              </div>
            </div>
            {autobotStats.autobot_stats.next_check !== 'N/A' && (
              <div className="mt-3 text-xs text-gray-600">
                Next check: {autobotStats.autobot_stats.next_check}
              </div>
            )}
          </Card>
        )}

        {/* Tickets List */}
        {loading ? (
          <Card className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading tickets...</p>
          </Card>
        ) : filteredTickets.length === 0 ? (
          <Card className="p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tickets Found</h3>
            <p className="text-gray-600">No support tickets match your filters.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredTickets.map((ticket) => {
              const categoryData = categories[ticket.category] || categories['general_support'];
              const CategoryIcon = categoryData.icon;
              const statusBadge = getStatusBadge(ticket.status);
              const priorityBadge = getPriorityBadge(ticket.priority);

              return (
                <Card key={ticket.id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className={`p-2 rounded-lg ${categoryData.color}`}>
                        <CategoryIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1 flex-wrap">
                          <span className="text-xs font-mono text-gray-400">
                            #{ticket.ticket_number}
                          </span>
                          <Badge className={statusBadge.color}>{statusBadge.label}</Badge>
                          <Badge className={priorityBadge.color}>{priorityBadge.label}</Badge>
                          {ticket.bot_responded && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              🤖 Bot Replied
                            </Badge>
                          )}
                          {ticket.bot_confidence && (
                            <span className="text-xs text-gray-500">
                              {Math.round(ticket.bot_confidence)}% confidence
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-gray-900 mb-1">{ticket.subject}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <User className="w-3 h-3" />
                            <span>{ticket.user_email}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>
                          {ticket.reply_count > 0 && (
                            <div className="flex items-center space-x-1">
                              <MessageSquare className="w-3 h-3" />
                              <span>{ticket.reply_count} replies</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchTicketDetail(ticket.id)}
                      className="flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render ticket detail
  const renderTicketDetail = () => {
    if (!selectedTicket) return null;

    const categoryData = categories[selectedTicket.category] || categories['general_support'];
    const CategoryIcon = categoryData.icon;
    const statusBadge = getStatusBadge(selectedTicket.status);
    const priorityBadge = getPriorityBadge(selectedTicket.priority);

    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => {
            setView('list');
            setSelectedTicket(null);
          }}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tickets
        </Button>

        {/* Ticket Header */}
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-4 flex-1">
              <div className={`p-3 rounded-lg ${categoryData.color}`}>
                <CategoryIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-xs font-mono text-gray-400">
                    #{selectedTicket.ticket_number}
                  </span>
                  <Badge className={priorityBadge.color}>{priorityBadge.label}</Badge>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedTicket.subject}
                </h2>
                <p className="text-gray-600 mb-4">{selectedTicket.description}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>{selectedTicket.user_name || selectedTicket.user_email}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Mail className="w-4 h-4" />
                    <span>{selectedTicket.user_email}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>Created {new Date(selectedTicket.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bot Processing Info */}
          {(selectedTicket.bot_processed || selectedTicket.bot_responded) && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">🤖 AI Autobot Analysis</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Processed:</span>
                    <span className="ml-1 font-medium text-gray-900">
                      {selectedTicket.bot_processed ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Responded:</span>
                    <span className="ml-1 font-medium text-gray-900">
                      {selectedTicket.bot_responded ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {selectedTicket.bot_confidence && (
                    <div>
                      <span className="text-gray-600">Confidence:</span>
                      <span className={`ml-1 font-bold ${
                        selectedTicket.bot_confidence >= 70 ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {Math.round(selectedTicket.bot_confidence)}%
                      </span>
                    </div>
                  )}
                  {selectedTicket.bot_processed_at && (
                    <div className="col-span-2 md:col-span-3">
                      <span className="text-gray-600">Processed at:</span>
                      <span className="ml-1 text-gray-900">
                        {new Date(selectedTicket.bot_processed_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Status Actions */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <Badge className={statusBadge.color}>{statusBadge.label}</Badge>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')}
                  disabled={selectedTicket.status === 'in_progress'}
                >
                  In Progress
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                  disabled={selectedTicket.status === 'resolved'}
                >
                  Resolved
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                  disabled={selectedTicket.status === 'closed'}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Conversation */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversation</h3>
          
          {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
            <div className="space-y-4 mb-6">
              {selectedTicket.replies.map((reply) => {
                const isBot = reply.is_bot || (reply.admin_name === 'AI Assistant');
                return (
                  <div
                    key={reply.id}
                    className={`p-4 rounded-lg ${
                      isBot
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200'
                        : reply.is_admin
                        ? 'bg-purple-50 border border-purple-200'
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2 flex-wrap">
                        <span className={`text-sm font-medium ${
                          isBot ? 'text-blue-900' : reply.is_admin ? 'text-purple-900' : 'text-gray-900'
                        }`}>
                          {isBot ? '🤖 AI Assistant' : reply.is_admin ? '👨‍💼 Support Team' : '👤 User'}
                        </span>
                        {reply.admin_name && !isBot && (
                          <span className="text-xs text-gray-500">({reply.admin_name})</span>
                        )}
                        {isBot && (
                          <>
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              Automated
                            </Badge>
                            {reply.bot_confidence && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                {Math.round(reply.bot_confidence)}% confidence
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(reply.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                    {isBot && reply.bot_confidence && reply.bot_confidence < 70 && (
                      <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                        ⚠️ Low confidence response - manual review recommended
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 mb-6">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No replies yet. Be the first to respond!</p>
            </div>
          )}

          {/* Reply Form */}
          {selectedTicket.status !== 'closed' && (
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Reply
              </label>
              <Textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your response here... User will receive email and in-app notification."
                rows={4}
                className="mb-3"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyMessage.trim()}
                >
                  {sendingReply ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 User will be notified via email and will see a notification in their dashboard
              </p>
            </div>
          )}

          {selectedTicket.status === 'closed' && (
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 text-center">
              <p className="text-gray-700">This ticket is closed and can no longer receive replies.</p>
            </div>
          )}
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {view === 'list' && renderTicketList()}
      {view === 'detail' && renderTicketDetail()}
    </div>
  );
};

export default SupportManagement;
